import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FormikErrors, FormikProps, withFormik } from "formik";
import { Button, IconButton } from "@chakra-ui/button";
import { Box, BoxProps, Divider, HStack, VStack } from "@chakra-ui/layout";
import { AdminApiClient } from "../api/adminApiClient";
import { CherryHubAdminApi as CherryHubAdminApi } from "../api/adminApiModels";
import { FormStack } from "../components/FormStack/FormStack";
import { useAdminApiRequest } from "../hooks/useApiRequest";
import { AdminApiEnumConfigurations } from "../enums/AdminApiEnumConfigurations";
import { ApiResult } from "../api/dataApiClient";
import { ISOCurrencyCodeList } from "../enums/ISOCurrencyCode";
import { ChevronUpIcon, DeleteIcon } from "@chakra-ui/icons";
import { PlusIcon, TrashIcon } from "../styles/icons";
import { FieldType } from "../enums/FieldType";
import { cloneDeep } from "lodash";
import { PaymentProvider, PaymentProviderList } from "../enums/PaymentProvider";
import { v4 as uuid } from "uuid";
import { usePaginatedAdminApiRequest } from "../hooks/usePaginatedApiRequest";
import * as Yup from "yup";
import { JsonTextAreaField } from "../components/fields/TextArea/JsonTextAreaField";
import { MemberFilterQueryModal } from "../components/MemberFilterQuery/MemberFilterQueryModal";
import { TextField } from "../components/fields/TextField/TextField";
import {
  ChakraReactSelectField,
  SelectOption,
} from "../components/fields/SelectField/ChakraReactSelectField";
import { TextArea } from "../components/fields/TextArea/TextArea";
import { SwitchField } from "../components/fields/SwitchField/SwitchField";
import { IntegerField } from "../components/fields/IntegerField/IntegerField";
import { HtmlTextArea } from "../components/fields/TextArea/HtmlTextArea";
import { CopyText } from "../components/Utilities/CopyText";
import { useDropzone } from "react-dropzone";
import { entriesKeyValue } from "../util/entriesObject";
import {
  CustomFieldSection,
  CustomFormField,
} from "../components/RegistrationFormConfiguration/RegistrationFormCustomFieldsSection";
import { TimePeriod } from "../components/RegistrationFormConfiguration/RegistrationFormTimePeriod";
import { useRegistrationCustomFieldModal } from "../hooks/useRegistrationCustomFieldModal";
import { ServiceType } from "../enums/ServiceType";
import { SingleValue } from "react-select";
import { useConfirmationDialog } from "../hooks/useConfirmationDialog";
import { ErrorMessage } from "../components/ErrorMessage/ErrorMessage";
import { useRegistrationKeyValuePairsModal } from "../hooks/useRegistrationKeyValuePairsModal";
import { useToast } from "../hooks/useToast";
import { KeyValuePairField } from "../components/RegistrationFormConfiguration/RegistrationFormKeyValuePairSection";
import { RegistrationFormCustomerRiskManagementFlowSection } from "../components/RegistrationFormConfiguration/RegistrationFormCustomerRiskManagementFlowSection";
import { CustomerRiskManagementThirdParty } from "../enums/CustomerRiskManagementThirdParty";

const emptyStrToNull = (value: string | null): string | null => {
  if (!value || value.trim() === "") {
    return null;
  } else {
    return value;
  }
};

type FieldMatchingConfiguration = {
  AlternateMemberNameMatchField?: CherryHubAdminApi.MatchableMemberField;
  ExactMatchScore?: number;
  NameDiminuitiveScore?: number | null;
  LevenshteinDistanceScores?: { key: string; value: number }[] | null;
  NameStartsWithPercentageOfLength?: number | null;
  NameStartsWithScore?: number | null;
  ClearMemberNameFieldValues?: string[] | null;
  TestFallbackMatchFieldWhenScoreLessThan?: number | null;
  FallbackMatchField?: {
    key: CherryHubAdminApi.MatchableMemberField;
    value: FieldMatchingConfiguration;
  } | null;
};

type MemberMatchingConfiguration = {
  FieldMatchingConfigurations?:
    | {
        key: CherryHubAdminApi.MatchableMemberField | undefined;
        value: FieldMatchingConfiguration;
      }[]
    | null;
  MinimumMatchingScoreToQualifyAsMatch?: number | null;
};

export type KvpFormField = {
  key: string;
  value: CherryHubAdminApi.CustomFormField[];
}[];

export const getFormFieldId = (
  formField: CherryHubAdminApi.CustomFormField | CustomFormField
) => {
  if (
    formField.FieldSpecification?.IsCustomIntegrationField ||
    formField.FieldSpecification?.IsNonIntegratedField
  ) {
    return formField.Identifier;
  }

  return formField.FieldSpecification?.SurrogateKey!;
};

export interface RegistrationFormConfigurationDetailFormValues {
  isEnabled?: boolean;
  redirectUrl?: string | null;
  friendlyName?: string | null;
  integrationId?: string | null;
  notificationEmailConfiguration?: CherryHubAdminApi.SendNotificationConfiguration;
  sendNewMemberNotificationEmail?: boolean;
  sendMemberReceiptEmail?: boolean;
  sendMemberReceiptEmailAfterMembershipCreation?: boolean;
  memberReceiptEmailConfiguration?: CherryHubAdminApi.SendNotificationConfiguration;
  sendMembershipSubmissionNotificationEmail?: boolean;
  submissionNotificationEmailConfiguration?: CherryHubAdminApi.SendNotificationConfiguration;
  noMembershipManagementService?: boolean;
  promotionId?: string | null;
  promotionConfigurationId?: string | null;
  oTPValidTimePeriod?: CherryHubAdminApi.TimePeriod;
  paymentConfiguration?: CherryHubAdminApi.PaymentConfiguration;
  twoStepFlowConfiguration?: CherryHubAdminApi.TwoStepFlowConfiguration;
  successfulSubmissionText?: string | null;
  adhocPassInvitationConfiguration?: CherryHubAdminApi.AdhocPassInvitationConfiguration;
  timeZoneId?: string | null;
  formFields?: CustomFormField[] | null;
  membershipTypeFormFieldIdentifier?: string | null;
  language?: string | null;
  pageTitle?: string | null;
  systemOnline?: boolean;
  oTPFlowRequired?: boolean;
  oTPValidForMinutes?: number;
  twoStepFlowRequired?: boolean;
  paymentRequired?: boolean;
  footerText?: string | null;
  themeData?: unknown | null;
  headerText?: string | null;
  logoFileUrl?: string | null;
  membershipName?: string | null;
  privacyPolicyUrl?: string | null;
  signeeAcceptanceText?: string | null;
  signeeInstructions?: string | null;
  termsConditionsUrl?: string | null;
  existingMemberCheckFilterQueryString?: string | null;
  checkExistingMember?: boolean;
  allowExistingMemberCheckOverride?: boolean;
  geoSuggestCountryFilter?: string[] | null;
  memberMatchingConfiguration?: MemberMatchingConfiguration | null;
  useFuzzyMemberMatching?: boolean;
  formFieldError?: CherryHubAdminApi.FormFieldError[];
  customerRiskManagementFlowEnabled?: boolean;
  customerRiskManagementFlowConfiguration?: CherryHubAdminApi.CustomerRiskManagementFlowConfiguration;
}

function convertToCustomFormField(field: CherryHubAdminApi.CustomFormField) {
  let result = { ...field } as CustomFormField;

  if (field.IntegrationObject)
    result.IntegrationObject = entriesKeyValue(field.IntegrationObject).map(
      ([key, value]) => {
        return {
          key,
          value:
            typeof value == "object"
              ? JSON.stringify(value, null, 2)
              : (value as string),
        };
      }
    );

  if (field.FeeAmounts)
    result.FeeAmounts = entriesKeyValue(field.FeeAmounts).map(
      ([key, value]) => {
        return { key, value };
      }
    );

  // Convert datetime strings to datetime-local format (yyyy-MM-ddTHH:mm)
  if (field.DateSelectedMinValue) {
    const date = new Date(field.DateSelectedMinValue);
    result.DateSelectedMinValue = formatDateTimeLocal(date);
  }

  if (field.DateSelectedMaxValue) {
    const date = new Date(field.DateSelectedMaxValue);
    result.DateSelectedMaxValue = formatDateTimeLocal(date);
  }

  return result;
}

function formatDateTimeLocal(date: Date): string {
  // Use UTC methods to avoid timezone conversion
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

interface RegistrationFormConfigurationDetailFormProps {
  apiClient: AdminApiClient;
  businessId: string;
  registration?: CherryHubAdminApi.RegistrationFormConfiguration | null;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess: (
    registration: CherryHubAdminApi.RegistrationFormConfiguration
  ) => void;
  onFailure: (message?: string) => void;
}

const InnerForm = ({
  apiClient,
  isSubmitting,
  isDisabled,
  submitForm,
  isValid,
  onCancel,
  values,
  errors,
  registration,
  businessId,
  setFieldValue,
}: RegistrationFormConfigurationDetailFormProps &
  FormikProps<RegistrationFormConfigurationDetailFormValues>) => {
  useState<CherryHubAdminApi.IntegrationSummaryItem>();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();
  const [isUploadingLogo, setIsUploadingLogo] = useState<boolean>(false);
  const [kvpFormFields, setKvpFormFields] = useState<KvpFormField>();
  const [requiredKvpFormFields, setRequiredKVPFormFields] =
    useState<KvpFormField>();
  const [selectedIntegration, setSelectedIntegration] =
    useState<CherryHubAdminApi.IntegrationSummaryItem | null>();
  const [isFormFieldError, setIsFormFieldError] = useState<boolean>(true);
  const { showSuccessToast, showErrorToast } = useToast();

  const onDropLogo = async (acceptedFiles: any) => {
    const file = acceptedFiles?.[0];

    if (!file) {
      return;
    }

    setIsUploadingLogo(true);

    try {
      const result =
        await apiClient.uploadBusinessRegistrationFormConfigurationImage(
          businessId,
          registration?.Meta?.Id!,
          "Logo",
          file
        );
      if (result.ok) {
        setFieldValue("logoFileUrl", result.data);
      }
    } catch (e) {
      setIsUploadingLogo(false);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const {
    getRootProps: getLogoRootProps,
    getInputProps: getLogoInputProps,
    isDragActive: isDragLogoActive,
  } = useDropzone({
    onDrop: onDropLogo,
    accept: { "image/jpeg": [".jpg", ".jpeg"], "image/png": [".png"] },
  });

  const { data: integrationOptions, isLoading: integrationOptionsLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getBusinessIntegrations(businessId, {
          limit: 1000,
          enabledService: ServiceType.MembershipManagementService,
        }),
      []
    );

  const { data: formFields, isLoading: formFieldsLoading } = useAdminApiRequest(
    (apiClient) =>
      values.integrationId
        ? apiClient.getMMSFormFields(businessId, values.integrationId)
        : null,
    [values.integrationId]
  );

  useEffect(() => {
    if (registration?.IntegrationId) {
      setSelectedIntegration(
        integrationOptions?.Results.find(
          (integration) => integration.Meta?.Id == registration.IntegrationId
        ) ?? null
      );
    } else {
      setSelectedIntegration(null);
    }
  }, [integrationOptions]);

  /**
   * Pre-populated Custom Field based on integrationId
   */
  useEffect(() => {
    const kvpFF = formFields
      ? Object.entries(formFields).map(([key, value]) => {
          return { key, value };
        })
      : [];
    setKvpFormFields(kvpFF);
    const requiredKvp = kvpFF?.filter(
      (df) => df.value[0].FieldSpecification?.IsSystemRequired
    );
    const requiredDfs = requiredKvp
      .map((df) => df.value[0])
      .map(convertToCustomFormField);
    if (!registration?.FormFields?.length) {
      setFieldValue("formFields", requiredDfs);
    }
    setRequiredKVPFormFields(requiredKvp);
  }, [formFields]);

  /**
   * Compute form field error state
   */
  useEffect(() => {
    let hasError = false;
    requiredKvpFormFields?.forEach((kvpFF) => {
      const filteredFields = values.formFields?.filter(
        (cf) => cf.Identifier == kvpFF.key
      );
      if (!filteredFields?.length || filteredFields.length > 1) {
        hasError = true;
      }
    });
    setIsFormFieldError(hasError);
  }, [requiredKvpFormFields, values.formFields]);

  const { modalElement: customFieldModalElement, showCustomFieldModal } =
    useRegistrationCustomFieldModal();

  const { modalElement: kvpModalElement, showKeyValuePairsModal } =
    useRegistrationKeyValuePairsModal();

  const addKeyValuePairs = (formField: CustomFormField) => {
    showKeyValuePairsModal(
      businessId,
      values.integrationId!,
      formField.Identifier,
      formField.KeyValuePairs?.map((kvp) => kvp.Key)
    ).then(({ ok, keyValuePairs }) => {
      if (ok && keyValuePairs && keyValuePairs.length) {
        const newFFIdx =
          values.formFields?.findIndex(
            (ff) => getFormFieldId(ff) == getFormFieldId(formField)
          ) ?? -1;
        if (newFFIdx != -1) {
          let newFF = values.formFields![newFFIdx].KeyValuePairs;
          if (!newFF) {
            newFF = [];
          }
          values.formFields![newFFIdx].KeyValuePairs =
            newFF.concat(keyValuePairs);
          setFieldValue("formFields", values.formFields);
        }
      }
    });
  };

  const validateKeyValuePairs = async (formField: CustomFormField) => {
    const result = await apiClient.validateMMSFormFieldKeyValuePairs(
      businessId,
      values.integrationId!,
      formField.Identifier,
      formField.KeyValuePairs?.map((ff) => ff.Key).filter((ff) => ff) ?? []
    );
    if (result.ok) {
      if (!result.data.HasErrors) {
        showSuccessToast("All Key Value Pairs has been validated.");
      } else {
        return (
          result.data.Errors?.map((err) => err.KeyValuePairsKey ?? "").filter(
            (err) => err
          ) ?? []
        );
      }
    } else if (result.message) {
      showErrorToast(result.message);
    }
    return [];
  };

  const replaceAllKeyValuePairs = (formField: CustomFormField) => {
    apiClient
      .getMMSFormFieldKeyValuePairs(
        businessId,
        values.integrationId!,
        formField.Identifier
      )
      .then((result) => {
        if (result.ok) {
          const newFFIdx =
            values.formFields?.findIndex(
              (ff) => getFormFieldId(ff) == getFormFieldId(formField)
            ) ?? -1;
          if (newFFIdx != -1) {
            values.formFields![newFFIdx].KeyValuePairs = result.data;
            setFieldValue("formFields", values.formFields);
          }
        } else if (result.message) {
          showErrorToast(result.message);
        }
      });
  };

  const {
    data: matchableMemberFieldOptions,
    isLoading: matchableMemberFieldOptionsLoading,
  } = useAdminApiRequest(
    (apiClient) =>
      apiClient.getEnumConfigurations(
        AdminApiEnumConfigurations.MatchableMemberField
      ),
    []
  );

  const { data: languageOptions, isLoading: languageOptionsLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getEnumConfigurations(
          AdminApiEnumConfigurations.SupportedLanguage
        ),
      []
    );

  const { data: timezoneOptions, isLoading: timezoneOptionsLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getEnumConfigurations(AdminApiEnumConfigurations.TimeZone),
      []
    );

  const { data: templateOptions, isLoading: templateOptionsLoading } =
    useAdminApiRequest((apiClient) => apiClient.getEmailTemplates(), []);

  const { data: countryOptions, isLoading: countryOptionsLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getEnumConfigurations(AdminApiEnumConfigurations.Country),
      []
    );

  const removeCustomFormField = (formFieldIdx: number) => {
    let removed = values.formFields?.splice(formFieldIdx, 1);
    setFieldValue("formFields", values.formFields);
  };

  const scrollToFormField = () => {
    setTimeout(
      () =>
        document
          .querySelector(`.custom-field-${values.formFields?.length! - 1}`)
          ?.scrollIntoView({ behavior: "smooth" }),
      500
    );
  };

  const addFormField = (
    formField: CherryHubAdminApi.CustomFormField,
    scrollTo?: boolean
  ) => {
    values.formFields?.push(convertToCustomFormField(formField));
    setFieldValue("formFields", values.formFields);
    if (scrollTo) {
      scrollToFormField();
    }
  };

  const addCustomFormField = (scrollTo?: boolean) => {
    const identifier = uuid();
    if (values.integrationId) {
      const results = cloneDeep(kvpFormFields) ?? [];
      kvpFormFields?.forEach((kvp, idxKvp) => {
        kvp.value.forEach((field, idxField) => {
          if (
            values.formFields?.find(
              (added) => getFormFieldId(added) == getFormFieldId(field)
            )
          ) {
            if (kvp.value.length == results[idxKvp].value.length) {
              results[idxKvp].value.splice(idxField, 1);
            } else {
              const resultsIdx = results[idxKvp].value.findIndex(
                (result) => getFormFieldId(result) == getFormFieldId(field)
              );
              results[idxKvp].value.splice(resultsIdx, 1);
            }
          }
        });
      });
      showCustomFieldModal(results.filter((field) => field.value.length)).then(
        ({ ok, customFields }) => {
          if (ok && customFields && customFields.length) {
            setFieldValue(
              "formFields",
              values.formFields?.concat(
                customFields.map(convertToCustomFormField)
              )
            );
          }
        }
      );
    } else if (values.formFields == null) {
      setFieldValue("formFields", [
        {
          Identifier: identifier,
          Name: "",
          Type: FieldType.Checkbox,
          IsVisible: true,
        },
      ]);
    } else {
      values.formFields?.push({
        Identifier: identifier,
        Name: "",
        Type: FieldType.Checkbox,
        IsVisible: true,
      });
      setFieldValue("formFields", values.formFields);
      if (scrollTo) {
        scrollToFormField();
      }
    }
  };

  const validateFormFields = (
    requiredKvpFF: KvpFormField | undefined = requiredKvpFormFields
  ) => {
    const view = requiredKvpFF?.map((kvpFF) => {
      const filteredFields = values.formFields?.filter(
        (cf) => cf.Identifier == kvpFF.key
      );
      if (!filteredFields?.length) {
        return (
          <ErrorMessage key={kvpFF.key}>
            <HStack>
              <Text fontSize="md">{`${kvpFF.key} is required!`}</Text>
              {kvpFF.value.map((formField, idx) => (
                <Button
                  key={getFormFieldId(formField)}
                  leftIcon={<PlusIcon />}
                  onClick={() => addFormField(formField, true)}
                  colorScheme="cherryButton"
                  alignSelf="flex-end"
                  size="sm"
                >
                  {formField.FieldSpecification?.SurrogateKey?.replace(
                    `${kvpFF.key}.`,
                    ""
                  )}
                </Button>
              ))}
            </HStack>
          </ErrorMessage>
        );
      } else if (filteredFields.length > 1) {
        return (
          <ErrorMessage key={kvpFF.key}>
            <HStack>
              <Text fontSize="md">{`${kvpFF.key} is duplicated!`}</Text>
              {filteredFields.map((formField, idx) => (
                <Button
                  key={getFormFieldId(formField)}
                  leftIcon={<DeleteIcon />}
                  onClick={() =>
                    removeCustomFormField(
                      values.formFields!.findIndex(
                        (field) =>
                          getFormFieldId(field) == getFormFieldId(formField)
                      )!
                    )
                  }
                  colorScheme="cherryButton"
                  alignSelf="flex-end"
                  size="sm"
                >
                  {formField.FieldSpecification?.SurrogateKey?.replace(
                    `${kvpFF.key}.`,
                    ""
                  )}
                </Button>
              ))}
            </HStack>
          </ErrorMessage>
        );
      }
    });

    return view;
  };

  const addFieldMatchingConfiguration = () => {
    if (!values.memberMatchingConfiguration) {
      values.memberMatchingConfiguration = {};
    }
    if (!values.memberMatchingConfiguration.FieldMatchingConfigurations) {
      values.memberMatchingConfiguration.FieldMatchingConfigurations = [];
    }
    values.memberMatchingConfiguration.FieldMatchingConfigurations!.push({
      key: undefined,
      value: {
        AlternateMemberNameMatchField: undefined,
        ExactMatchScore: undefined,
        NameDiminuitiveScore: null,
        LevenshteinDistanceScores: null,
        NameStartsWithPercentageOfLength: null,
        NameStartsWithScore: null,
        ClearMemberNameFieldValues: null,
        TestFallbackMatchFieldWhenScoreLessThan: null,
        FallbackMatchField: null,
      },
    });
    setFieldValue(
      "memberMatchingConfiguration.FieldMatchingConfigurations",
      values.memberMatchingConfiguration.FieldMatchingConfigurations
    );
  };

  const {
    refresh,
    items: digitalMembershipCards,
    isLoading,
  } = usePaginatedAdminApiRequest(
    // Request the first page
    (apiClient) =>
      apiClient.getBusinessDigitalMembershipCardPassConfiguration(businessId!),
    // Request subsequent pages
    (apiClient, _, continuationToken) =>
      apiClient.getBusinessDigitalMembershipCardPassConfiguration(
        businessId,
        continuationToken
      ),
    []
  );

  const digitalMembershipCardOptions = useMemo(
    () =>
      digitalMembershipCards.map((cards) => ({
        label: cards.DisplayName,
        value: cards.Meta?.Id!.replace(`${businessId}.`, "") ?? "",
      })),
    [digitalMembershipCards]
  );

  return (
    <>
      {confirmationElement}
      {customFieldModalElement}
      {kvpModalElement}
      <Box width="100%" alignSelf="flex-start">
        <FormStack>
          {registration && (
            <HStack w="100%">
              <Box flexGrow={2} pb={4}>
                <CopyText text={registration?.Meta?.Id!} hover adjustMargin>
                  <Text fontSize="md" color="cherryUi.600">
                    {registration?.Meta?.Id} /{" "}
                    {registration?.RegistrationFormConfigurationNo}
                  </Text>
                </CopyText>
                <Text fontSize="xl" color="cherryUi.600">
                  <b>{registration?.FriendlyName}</b>
                </Text>
              </Box>

              <Box>
                <Button
                  colorScheme="cherryButton"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting || !isValid || isFormFieldError}
                  onClick={submitForm}
                >
                  Save
                </Button>
              </Box>

              <Box>
                <Button disabled={isSubmitting} onClick={onCancel}>
                  Cancel
                </Button>
              </Box>
            </HStack>
          )}

          {/* <HStack w="100%" spacing={4}> */}
          <VStack
            w="100%"
            maxW="1200px"
            alignItems="flex-start"
            pb={10}
            flex={3}
          >
            <TextField horizontal label="Friendly Name" name="friendlyName" />

            <SwitchField horizontal label="Enabled" name="isEnabled" />

            <Box w="100%">
              <TextField horizontal label="Redirect URL" name="redirectUrl" />
              <Text fontSize="xs" color="cherryGrey.600" mt={1} ml="25%">
                The redirect URL only takes effect when the form is disabled
              </Text>
            </Box>

            <ChakraReactSelectField
              horizontal
              label="Integration Id"
              name="integrationId"
              selectedOptionColor="blue"
              isLoading={
                integrationOptionsLoading || selectedIntegration === undefined
              }
              allowEmpty={selectedIntegration ? false : "No Integration"}
              onChange={(integration) => {
                const integrationValue =
                  integration as SingleValue<SelectOption>;
                const currSourceSystemName = integrationOptions?.Results.find(
                  (opt) => opt.Meta?.Id == integrationValue?.value
                )?.SourceSystemName;
                if (
                  values.integrationId == null ||
                  currSourceSystemName ==
                    integrationOptions?.Results.find(
                      (opt) => opt.Meta?.Id == values.integrationId
                    )?.SourceSystemName
                ) {
                  setFieldValue("integrationId", integrationValue?.value);
                } else {
                  showConfirmationDialog(
                    "Integration change confirmation",
                    "Changing integration id will reset all the custom field. \n Are you sure want to proceed?",
                    "Reset Custom Field"
                  ).then(({ ok }) => {
                    if (ok) {
                      setFieldValue("integrationId", integrationValue?.value);
                    } else {
                      setFieldValue("integrationId", values.integrationId);
                    }
                  });
                }
              }}
              options={
                integrationOptions?.Results.filter((integration) =>
                  selectedIntegration
                    ? integration.SourceSystemName ==
                      selectedIntegration.SourceSystemName
                    : true
                ).map((integration) => {
                  return {
                    label: integration.DisplayName!,
                    value: integration.Meta!.Id!,
                  };
                }) || []
              }
            />

            <ChakraReactSelectField
              horizontal
              label="Language "
              name="language"
              selectedOptionColor="blue"
              isLoading={languageOptionsLoading}
              options={
                languageOptions?.map((provider) => {
                  return {
                    label: provider.Value!,
                    value: provider.Key!,
                  };
                }) || []
              }
            />

            <TextField horizontal label="Page Title" name="pageTitle" />

            <TextField
              horizontal
              label="Membership Name"
              name="membershipName"
            />

            <TextField horizontal label="Logo File Url" name="logoFileUrl" />

            <TextField
              horizontal
              label="Privacy Policy Url"
              name="privacyPolicyUrl"
            />

            <TextField
              horizontal
              label="Terms Conditions Url"
              name="termsConditionsUrl"
            />

            <ChakraReactSelectField
              horizontal
              label="Time Zone Id"
              name="timeZoneId"
              selectedOptionColor="blue"
              isLoading={timezoneOptionsLoading}
              className="relative-listbox"
              options={
                timezoneOptions?.map((provider) => {
                  return {
                    label: provider.Value!,
                    value: provider.Key!,
                  };
                }) || []
              }
            />
          </VStack>
          {/* <Box w="100%" flex={1} alignSelf="flex-start">
              {registration && (
                <DropZoneFileUploadField<AdminApiClient>
                  label="Logo"
                  name="logoFileUrl"
                  apiClient={apiClient}
                  uploadSuccessText="Logo has been updated"
                  uploadFile={(apiClient, file) =>
                    apiClient.uploadBusinessRegistrationFormConfigurationImage(
                      businessId!,
                      registration.Meta?.Id!,
                      RegistrationImageType.Logo,
                      file
                    )
                  }
                />
              )}
            </Box>
          </HStack> */}

          <Tabs variant="edit" w="100%" isLazy>
            <TabList>
              <HStack spacing={3}>
                <Tab>Form Setup</Tab>

                <Tab>Fields</Tab>

                {/* <Tab>Display</Tab> */}

                <Tab>Advanced</Tab>
              </HStack>
            </TabList>

            <TabPanels>
              <TabPanel>
                <VStack w="100%">
                  <Accordion
                    w="100%"
                    allowToggle
                    allowMultiple
                    variant="section"
                    defaultIndex={[0, 1, 2, 3, 4]}
                  >
                    <AccordionItem>
                      {({ isExpanded }) => (
                        <>
                          <h2>
                            <AccordionButton>
                              <Box flex="1" textAlign="left">
                                <b>Form Header and Footer Content</b>
                              </Box>
                              {isExpanded ? (
                                <ChevronUpIcon color="white" fontSize="xl" />
                              ) : (
                                <AccordionIcon />
                              )}
                            </AccordionButton>
                          </h2>
                          <AccordionPanel width="100%">
                            <VStack w="100%" alignItems="flex-start">
                              <HtmlTextArea
                                horizontal
                                label="Header Text"
                                name="headerText"
                              />

                              <HtmlTextArea
                                horizontal
                                label="Footer Text"
                                name="footerText"
                              />

                              <HtmlTextArea
                                horizontal
                                label="Signee Instructions"
                                name="signeeInstructions"
                              />

                              <HtmlTextArea
                                horizontal
                                label="Signee Acceptance Text"
                                name="signeeAcceptanceText"
                              />

                              <HtmlTextArea
                                horizontal
                                label="Successful Submission Text"
                                name="successfulSubmissionText"
                              />
                            </VStack>
                          </AccordionPanel>
                        </>
                      )}
                    </AccordionItem>

                    {/* <OTPSection
                      oTPFlowRequired={values.oTPFlowRequired}
                      oTPValidForMinutes={values.oTPValidForMinutes}
                      oTPValidTimePeriod={values.oTPValidTimePeriod}
                    /> */}

                    <PaymentSection
                      paymentRequired={values.paymentRequired}
                      paymentConfiguration={values.paymentConfiguration}
                    />

                    <TwoStepFlowSection
                      twoStepFlowRequired={values.twoStepFlowRequired}
                      twoStepFlowConfiguration={values.twoStepFlowConfiguration}
                      sendMembershipSubmissionNotificationEmail={
                        values.sendMembershipSubmissionNotificationEmail
                      }
                      digitalMembershipCardOptions={
                        digitalMembershipCardOptions
                      }
                      submissionNotificationEmailConfiguration={
                        values.submissionNotificationEmailConfiguration
                      }
                      templateOptions={templateOptions}
                      templateOptionsLoading={templateOptionsLoading}
                      setFieldValue={setFieldValue}
                    />

                    <NotificationSection
                      notificationEmailConfiguration={
                        values.notificationEmailConfiguration
                      }
                      sendNewMemberNotificationEmail={
                        values.sendNewMemberNotificationEmail
                      }
                      sendMemberReceiptEmail={values.sendMemberReceiptEmail}
                      sendMemberReceiptEmailAfterMembershipCreation={
                        values.sendMemberReceiptEmailAfterMembershipCreation
                      }
                      memberReceiptEmailConfiguration={
                        values.memberReceiptEmailConfiguration
                      }
                      templateOptions={templateOptions}
                      templateOptionsLoading={templateOptionsLoading}
                      setFieldValue={setFieldValue}
                    />

                    <RegistrationFormCustomerRiskManagementFlowSection
                      customerRiskManagementFlowEnabled={
                        values.customerRiskManagementFlowEnabled
                      }
                      customerRiskManagementFlowConfiguration={
                        values.customerRiskManagementFlowConfiguration
                      }
                    />
                  </Accordion>
                </VStack>
              </TabPanel>

              <TabPanel>
                <VStack w="100%">
                  <CustomFieldSection
                    customFormFields={values.formFields}
                    addCustomFormField={addCustomFormField}
                    addKeyValuePairs={addKeyValuePairs}
                    validateKeyValuePairs={validateKeyValuePairs}
                    replaceAllKeyValuePairs={replaceAllKeyValuePairs}
                    removeCustomFormField={removeCustomFormField}
                    setFieldValue={setFieldValue}
                    validateFormFields={validateFormFields}
                    errors={errors}
                    twoStepFlowRequired={values.twoStepFlowRequired}
                  />
                </VStack>
              </TabPanel>

              <TabPanel>
                <VStack w="100%" alignItems="flex-start">
                  <AdvancedConfigurationSection
                    noMembershipManagementService={
                      values.noMembershipManagementService
                    }
                    promotionConfigurationId={values.promotionConfigurationId}
                    promotionId={values.promotionId}
                    checkExistingMember={values.checkExistingMember}
                    existingMemberCheckFilterQueryString={
                      values.existingMemberCheckFilterQueryString
                    }
                    businessId={businessId}
                    countryOptions={countryOptions}
                    countryOptionsLoading={countryOptionsLoading}
                    setFieldValue={setFieldValue}
                  />

                  <Accordion
                    w="100%"
                    allowToggle
                    allowMultiple
                    variant="section"
                    defaultIndex={[0]}
                  >
                    {values.checkExistingMember &&
                      values.useFuzzyMemberMatching && (
                        <MemberMatchingConfigurationSection
                          memberMatchingConfiguration={
                            values.memberMatchingConfiguration
                          }
                          isLoading={matchableMemberFieldOptionsLoading}
                          matchableMemberFieldOptions={
                            matchableMemberFieldOptions
                          }
                          setFieldValue={setFieldValue}
                          addFieldMatchingConfiguration={
                            addFieldMatchingConfiguration
                          }
                          errors={errors}
                        />
                      )}

                    <AdhocPassInvitationSection
                      adhocPassInvitationConfiguration={
                        values.adhocPassInvitationConfiguration
                      }
                    />

                    <ThemeDataSection
                      themeData={values.themeData}
                      errors={errors.themeData}
                      setFieldValue={setFieldValue}
                    />
                  </Accordion>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>

          {values.formFieldError?.map((err) => (
            <ErrorMessage>
              {`${err.FieldIdentifier} : ${err.ErrorMessage}`}
            </ErrorMessage>
          ))}

          <HStack width="100%" justifyContent="center" spacing="3" pt={8}>
            <Button
              colorScheme="cherryButton"
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting || !isValid || isFormFieldError}
              onClick={submitForm}
            >
              Save
            </Button>
            <Button disabled={isSubmitting} onClick={onCancel}>
              Cancel
            </Button>
          </HStack>
        </FormStack>
      </Box>
    </>
  );
};

export const RegistrationFormConfigurationDetailForm = withFormik<
  RegistrationFormConfigurationDetailFormProps,
  RegistrationFormConfigurationDetailFormValues
>({
  mapPropsToValues: ({ registration }) => {
    const initalValue: RegistrationFormConfigurationDetailFormValues = {
      isEnabled: registration?.IsEnabled,
      redirectUrl: registration?.RedirectUrl ?? "",
      friendlyName: registration?.FriendlyName ?? "",
      integrationId: registration?.IntegrationId,
      notificationEmailConfiguration:
        registration?.NotificationEmailConfiguration,
      sendNewMemberNotificationEmail:
        registration?.SendNewMemberNotificationEmail,
      sendMemberReceiptEmail: registration?.SendMemberReceiptEmail,
      sendMemberReceiptEmailAfterMembershipCreation:
        registration?.SendMemberReceiptEmailAfterMembershipCreation,
      memberReceiptEmailConfiguration:
        registration?.MemberReceiptEmailConfiguration,
      sendMembershipSubmissionNotificationEmail:
        registration?.SendMembershipSubmissionNotificationEmail,
      submissionNotificationEmailConfiguration:
        registration?.SubmissionNotificationEmailConfiguration,
      noMembershipManagementService:
        registration?.NoMembershipManagementService,
      promotionId: registration?.PromotionId ?? "",
      promotionConfigurationId: registration?.PromotionConfigurationId ?? "",
      oTPValidTimePeriod: registration?.OTPValidTimePeriod,
      paymentConfiguration: registration?.PaymentConfiguration,
      twoStepFlowConfiguration: registration?.TwoStepFlowConfiguration,
      successfulSubmissionText: registration?.SuccessfulSubmissionText ?? "",
      adhocPassInvitationConfiguration:
        registration?.AdhocPassInvitationConfiguration,
      timeZoneId: registration?.TimeZoneId,
      formFields: registration?.FormFields?.map(convertToCustomFormField),
      language: registration?.Language?.LanguageCode,
      pageTitle: registration?.PageTitle ?? "",
      systemOnline: registration?.SystemOnline,
      oTPFlowRequired: registration?.OTPFlowRequired,
      oTPValidForMinutes: registration?.OTPValidForMinutes,
      twoStepFlowRequired: registration?.TwoStepFlowRequired,
      paymentRequired: registration?.PaymentRequired,
      footerText: registration?.FooterText ?? "",
      themeData: !registration?.ThemeData
        ? ""
        : JSON.stringify(registration?.ThemeData, null, 2),
      headerText: registration?.HeaderText ?? "",
      logoFileUrl: registration?.LogoFileUrl ?? "",
      membershipName: registration?.MembershipName ?? "",
      privacyPolicyUrl: registration?.PrivacyPolicyUrl ?? "",
      signeeAcceptanceText: registration?.SigneeAcceptanceText ?? "",
      signeeInstructions: registration?.SigneeInstructions ?? "",
      termsConditionsUrl: registration?.TermsConditionsUrl ?? "",
      existingMemberCheckFilterQueryString:
        registration?.ExistingMemberCheckFilterQueryString,
      checkExistingMember: registration?.CheckExistingMember,
      allowExistingMemberCheckOverride:
        registration?.AllowExistingMemberCheckOverride,
      geoSuggestCountryFilter: registration?.GeoSuggestCountryFilter,
      customerRiskManagementFlowEnabled:
        registration?.CustomerRiskManagementFlowEnabled,
      customerRiskManagementFlowConfiguration:
        registration?.CustomerRiskManagementFlowConfiguration
          ? {
              ...registration.CustomerRiskManagementFlowConfiguration,
              UseFullPageRedirect:
                registration.CustomerRiskManagementFlowConfiguration
                  .UseFullPageRedirect ?? false,
            }
          : undefined,
      useFuzzyMemberMatching: registration?.UseFuzzyMemberMatching,
      memberMatchingConfiguration: registration?.MemberMatchingConfiguration
        ? {
            ...registration.MemberMatchingConfiguration,
            FieldMatchingConfigurations:
              registration.MemberMatchingConfiguration.FieldMatchingConfigurations?.map(
                (fieldMatchingConfiguration) => {
                  const levenshteinDistanceScoreResult =
                    fieldMatchingConfiguration.Value?.LevenshteinDistanceScores?.map(
                      (levenshteinDistanceScore) => {
                        return {
                          key: levenshteinDistanceScore.Key!.toString(),
                          value: levenshteinDistanceScore.Value!,
                        };
                      }
                    ) ?? [];

                  const fallbackResult = fieldMatchingConfiguration.Value
                    ?.FallbackMatchField
                    ? {
                        key: fieldMatchingConfiguration.Value.FallbackMatchField
                          .Key as CherryHubAdminApi.MatchableMemberField,
                        value: {
                          ...fieldMatchingConfiguration.Value.FallbackMatchField
                            .Value,
                          LevenshteinDistanceScores:
                            fieldMatchingConfiguration.Value.FallbackMatchField?.Value?.LevenshteinDistanceScores?.map(
                              (levenshteinDistanceScoreFallback) => {
                                return {
                                  key: levenshteinDistanceScoreFallback.Key!.toString(),
                                  value:
                                    levenshteinDistanceScoreFallback.Value!,
                                };
                              }
                            ) ?? [],
                          FallbackMatchField: null,
                        },
                      }
                    : null;

                  const fieldMatchingConfigurationResult = {
                    ...fieldMatchingConfiguration.Value,
                    LevenshteinDistanceScores: levenshteinDistanceScoreResult,
                    FallbackMatchField: fallbackResult,
                  };

                  return {
                    key: fieldMatchingConfiguration.Key as CherryHubAdminApi.MatchableMemberField,
                    value: fieldMatchingConfigurationResult!,
                  };
                }
              ),
          }
        : null,
    };
    return initalValue;
  },

  handleSubmit: async (values, { props, setFieldError, setFieldValue }) => {
    if (values.friendlyName == null) {
      setFieldError("friendlyName", "Friendly Name is required.");
      return;
    }

    if (values.useFuzzyMemberMatching) {
      if (
        !values.memberMatchingConfiguration ||
        !values.memberMatchingConfiguration?.FieldMatchingConfigurations?.length
      ) {
        setFieldError(
          "useFuzzyMemberMatching",
          "Member Matching Configuration (including Field Matching Configurations) is required."
        );
        return;
      }

      let isValid = true;

      values.memberMatchingConfiguration?.FieldMatchingConfigurations.forEach(
        (fieldMatchinngConfiguration, fieldMatchinngConfigurationIdx) => {
          if (
            values.memberMatchingConfiguration?.FieldMatchingConfigurations?.filter(
              (compare, compareIdx) =>
                compareIdx != fieldMatchinngConfigurationIdx &&
                compare.key == fieldMatchinngConfiguration.key
            )?.length ??
            0 > 1
          ) {
            setFieldError(
              `memberMatchingConfiguration.FieldMatchingConfigurations[${fieldMatchinngConfigurationIdx}].key`,
              "Field Matching Configuration Id should be unique across the Member Matching Configuration"
            );
            isValid = false;
          }
        }
      );

      if (!isValid) return false;
    }

    // Translate form state to IntegrationUpdate
    const registrationDoc: Partial<CherryHubAdminApi.RegistratinFormConfigurationUpdate> =
      {
        IsEnabled: values.isEnabled,
        RedirectUrl: values.redirectUrl,
        FriendlyName: values.friendlyName,
        IntegrationId: values.integrationId,
        NotificationEmailConfiguration: values.notificationEmailConfiguration,
        SendNewMemberNotificationEmail: values.sendNewMemberNotificationEmail,
        SendMemberReceiptEmail: values.sendMemberReceiptEmail,
        SendMemberReceiptEmailAfterMembershipCreation:
          values.sendMemberReceiptEmailAfterMembershipCreation,
        MemberReceiptEmailConfiguration: values.memberReceiptEmailConfiguration,
        SendMembershipSubmissionNotificationEmail:
          values.sendMembershipSubmissionNotificationEmail,
        SubmissionNotificationEmailConfiguration:
          values.submissionNotificationEmailConfiguration,
        NoMembershipManagementService: values.noMembershipManagementService,
        PromotionId: values.promotionId,
        PromotionConfigurationId: values.promotionConfigurationId,
        OTPValidTimePeriod: values.oTPValidTimePeriod,
        PaymentConfiguration: values.paymentConfiguration,
        TwoStepFlowConfiguration: values.twoStepFlowConfiguration,
        CustomerRiskManagementFlowConfiguration: (() => {
          const config = values.customerRiskManagementFlowConfiguration;
          if (!config?.WorkflowTimeToLive) return config;
          if (
            !config.WorkflowTimeToLive.Quantity &&
            !config.WorkflowTimeToLive.Period
          ) {
            const { WorkflowTimeToLive, ...configWithoutWorkflowTTL } = config;
            return configWithoutWorkflowTTL;
          }
          if (!config.WorkflowTimeToLive.Quantity) {
            config.WorkflowTimeToLive.Quantity = 0;
          }
          if (!config.WorkflowTimeToLive.Period) {
            config.WorkflowTimeToLive.Period = "Second";
          }
          return config;
        })(),
        SuccessfulSubmissionText: values.successfulSubmissionText,
        AdhocPassInvitationConfiguration:
          values.adhocPassInvitationConfiguration,
        TimeZoneId: values.timeZoneId,
        FormFields:
          values.formFields?.map((field) => {
            let result = {
              ...field,
            } as CherryHubAdminApi.CustomFormField;
            delete result.IntegrationObject;
            delete result.FeeAmounts;

            if (
              !result.DateSelectedMaxRelativeTimePeriod?.Period &&
              !result.DateSelectedMaxRelativeTimePeriod?.Quantity
            ) {
              delete result.DateSelectedMaxRelativeTimePeriod;
            }

            if (
              !result.DateSelectedMinRelativeTimePeriod?.Period &&
              !result.DateSelectedMinRelativeTimePeriod?.Quantity
            ) {
              delete result.DateSelectedMinRelativeTimePeriod;
            }

            // Convert datetime-local format back to ISO string for API
            // Treat the datetime-local value as UTC to avoid timezone conversion
            if (result.DateSelectedMinValue) {
              const dateStr = result.DateSelectedMinValue;
              // Parse as UTC by appending 'Z' to treat it as UTC
              result.DateSelectedMinValue = new Date(
                dateStr + "Z"
              ).toISOString();
            }

            if (result.DateSelectedMaxValue) {
              const dateStr = result.DateSelectedMaxValue;
              // Parse as UTC by appending 'Z' to treat it as UTC
              result.DateSelectedMaxValue = new Date(
                dateStr + "Z"
              ).toISOString();
            }

            field.IntegrationObject?.forEach((kvp) => {
              if (!result.IntegrationObject) result.IntegrationObject = {};
              if (emptyStrToNull(values.themeData as string) == null)
                result.IntegrationObject[kvp.key] = null;
              try {
                result.IntegrationObject[kvp.key] = JSON.parse(
                  kvp.value as string
                );
              } catch {
                result.IntegrationObject[kvp.key] = kvp.value as string;
              }
            });

            field.FeeAmounts?.forEach((kvp) => {
              if (!result.FeeAmounts) result.FeeAmounts = {};
              result.FeeAmounts[kvp.key] = kvp.value;
            });
            return result;
          }) ?? [],
        Language: { LanguageCode: values.language },
        PageTitle: values.pageTitle,
        SystemOnline: values.systemOnline,
        OTPFlowRequired: values.oTPFlowRequired,
        OTPValidForMinutes: values.oTPValidForMinutes,
        TwoStepFlowRequired: values.twoStepFlowRequired,
        PaymentRequired: values.paymentRequired,
        CustomerRiskManagementFlowEnabled:
          values.customerRiskManagementFlowEnabled,
        FooterText: values.footerText,
        ThemeData:
          emptyStrToNull(values.themeData as string) == null
            ? null
            : JSON.parse(values.themeData as string),
        HeaderText: values.headerText,
        LogoFileUrl: values.logoFileUrl,
        MembershipName: values.membershipName,
        PrivacyPolicyUrl: values.privacyPolicyUrl,
        SigneeAcceptanceText: values.signeeAcceptanceText,
        SigneeInstructions: values.signeeInstructions,
        TermsConditionsUrl: values.termsConditionsUrl,
        ExistingMemberCheckFilterQueryString:
          values.existingMemberCheckFilterQueryString,
        CheckExistingMember: values.checkExistingMember,
        AllowExistingMemberCheckOverride:
          values.allowExistingMemberCheckOverride,
        GeoSuggestCountryFilter: values.geoSuggestCountryFilter,
        UseFuzzyMemberMatching: values.useFuzzyMemberMatching,
        MemberMatchingConfiguration: values.memberMatchingConfiguration
          ? {
              ...values.memberMatchingConfiguration,
              FieldMatchingConfigurations:
                values.memberMatchingConfiguration?.FieldMatchingConfigurations?.map(
                  (fieldMatchingConfiguration) => {
                    var fallbackFieldMatchingConfigurationResult =
                      fieldMatchingConfiguration.value.FallbackMatchField
                        ? {
                            ...fieldMatchingConfiguration.value
                              .FallbackMatchField?.value,
                            LevenshteinDistanceScores:
                              fieldMatchingConfiguration.value.FallbackMatchField.value.LevenshteinDistanceScores?.map(
                                (fallbackLevenshtein) => {
                                  return {
                                    Key: parseInt(fallbackLevenshtein.key),
                                    Value: fallbackLevenshtein.value,
                                  };
                                }
                              ),
                            FallbackMatchField: undefined,
                          }
                        : undefined;

                    var fieldMatchingConfigurationResult = {
                      ...fieldMatchingConfiguration.value,
                      LevenshteinDistanceScores:
                        fieldMatchingConfiguration.value.LevenshteinDistanceScores?.map(
                          (levenshtein) => {
                            return {
                              Key: parseInt(levenshtein.key),
                              Value: levenshtein.value,
                            };
                          }
                        ),
                      FallbackMatchField: fieldMatchingConfiguration.value
                        .FallbackMatchField
                        ? {
                            Key: fieldMatchingConfiguration.value
                              .FallbackMatchField
                              .key as CherryHubAdminApi.MatchableMemberField,
                            Value: fallbackFieldMatchingConfigurationResult,
                          }
                        : undefined,
                    };

                    return {
                      Key: fieldMatchingConfiguration.key as CherryHubAdminApi.MatchableMemberField,
                      Value: fieldMatchingConfigurationResult,
                    };
                  }
                ) ?? null,
            }
          : undefined,
      };

    if (values.twoStepFlowConfiguration?.PassInvitationHostConfiguration) {
      if (
        !values.twoStepFlowConfiguration.PassInvitationHostConfiguration
          .LandingPageUrlFormat
      )
        values.twoStepFlowConfiguration.PassInvitationHostConfiguration.LandingPageUrlFormat =
          "";

      if (
        !values.twoStepFlowConfiguration.PassInvitationHostConfiguration
          .EmailSenderEmailAddress
      )
        values.twoStepFlowConfiguration.PassInvitationHostConfiguration.EmailSenderEmailAddress =
          "";

      if (
        !values.twoStepFlowConfiguration.PassInvitationHostConfiguration
          .DownloadFromAppUrlFormat
      )
        values.twoStepFlowConfiguration.PassInvitationHostConfiguration.DownloadFromAppUrlFormat =
          "";

      if (
        !values.twoStepFlowConfiguration.PassInvitationHostConfiguration
          .LandingPageErrorUrlFormat
      )
        values.twoStepFlowConfiguration.PassInvitationHostConfiguration.LandingPageErrorUrlFormat =
          "";

      if (
        !values.twoStepFlowConfiguration.PassInvitationHostConfiguration
          .DownloadFromInvitationUrlFormat
      )
        values.twoStepFlowConfiguration.PassInvitationHostConfiguration.DownloadFromInvitationUrlFormat =
          "";
    }

    let result: ApiResult<CherryHubAdminApi.RegistrationFormConfiguration> | null =
      null;

    let isValid = true;

    if (values.integrationId && registrationDoc.FormFields?.length) {
      const validationResult = await props.apiClient.validateMMSFormField(
        props.businessId,
        values.integrationId,
        registrationDoc.FormFields
      );

      isValid = validationResult.ok && !validationResult.data.HasErrors;
      if (isValid) {
        setFieldValue("formFieldError", []);
      } else {
        setFieldValue("formFieldError", validationResult.data?.Errors);
        if (props.onFailure) {
          props.onFailure(
            "There is one or more form fields validation error(s)"
          );
        }
        return false;
      }
    }

    if (props.registration == null) {
      result =
        await props.apiClient.createBusinessRegistrationFormConfiguration(
          props.businessId,
          registrationDoc
        );
    } else {
      result =
        await props.apiClient.updateBusinessRegistrationFormConfiguration(
          props.businessId,
          props.registration.id,
          registrationDoc
        );
    }

    if (result.ok) {
      if (props.onSuccess) {
        props.onSuccess(result.data);
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message);
      }
    }
  },

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: true,

  validate: (values) => {
    let errors: FormikErrors<RegistrationFormConfigurationDetailFormValues> =
      {};
    try {
      if (emptyStrToNull(values.themeData as string) != null)
        JSON.parse(values.themeData as string);
    } catch (e) {
      errors.themeData = "Invalid json format.";
    }

    return errors;
  },
  validationSchema: Yup.object().shape({
    redirectUrl: Yup.string()
      .url("Redirect URL must be a valid URL")
      .nullable()
      .notRequired(),
    membershipName: Yup.string().required(
      "Membership Name is a required field."
    ),
    formFields: Yup.array().of(
      Yup.object().shape({
        IntegrationObject: Yup.array().of(
          Yup.object().shape({
            value: Yup.string().test(
              "jsonInvalid",
              "Invalid json format",
              (value) => {
                if (emptyStrToNull(value as string) == null) return true;
                const notJson =
                  (value as string).indexOf("[") == -1 &&
                  (value as string).indexOf("{") == -1;
                if (notJson) return true;
                try {
                  JSON.parse(value as string);
                } catch {
                  return false;
                }
                return true;
              }
            ),
          })
        ),
        KeyValuePairs: Yup.array().test(
          "kvpValidation",
          "Key Value Pairs is a required fields for Listbox Group",
          function (value) {
            const { Type } = this.parent;
            return Type == FieldType.ListboxGroup
              ? (value?.length ?? 0) > 0
              : true;
          }
        ),
        ThirdPartyDataFieldDataletConfigurations: Yup.array()
          .notRequired()
          .test(
            "thirdPartyIdentifierRequired",
            "Third Party Identifier is required for all third party data configurations",
            function (value) {
              const { AcceptThirdPartyData } = this.parent;
              if (!AcceptThirdPartyData || !value || value.length === 0) {
                return true;
              }
              return value.every(
                (config: any) =>
                  config.ThirdPartyIdentifier &&
                  config.ThirdPartyIdentifier.trim() !== ""
              );
            }
          )
          .test(
            "jsonPathRequired",
            "Json Path is required for all third party data configurations",
            function (value) {
              const { AcceptThirdPartyData } = this.parent;
              if (!AcceptThirdPartyData || !value || value.length === 0) {
                return true;
              }
              return value.every(
                (config: any) =>
                  config.JsonPath && config.JsonPath.trim() !== ""
              );
            }
          )
          .test(
            "thirdPartyIdentifierExclusivity",
            "Use either a single configuration with '*' identifier, or multiple configurations with specific identifiers (not '*')",
            function (value) {
              const { AcceptThirdPartyData } = this.parent;
              if (!AcceptThirdPartyData || !value || value.length === 0) {
                return true;
              }
              const hasWildcard = value.some(
                (config: any) => config.ThirdPartyIdentifier?.trim() === "*"
              );
              if (hasWildcard) {
                return value.length === 1;
              }
              return true;
            }
          ),
      })
    ),
    memberMatchingConfiguration: Yup.object()
      .notRequired()
      .shape({
        FieldMatchingConfigurations: Yup.array()
          .notRequired()
          .of(
            Yup.object().shape({
              key: Yup.string().required(
                "Field Matching Configuration Key is required"
              ),
              value: Yup.object().shape({
                FallbackMatchField: Yup.object()
                  .notRequired()
                  .shape({
                    key: Yup.string().required(
                      "Fallback Match Field Key is required"
                    ),
                    value: Yup.object().shape({
                      ExactMatchScore: Yup.number().required(
                        "Exact Match Score is required"
                      ),
                      LevenshteinDistanceScores: Yup.array()
                        .notRequired()
                        .of(
                          Yup.object().shape({
                            key: Yup.string().required("Key is required"),
                            value: Yup.string().required("Value is required"),
                          })
                        ),
                      ClearMemberNameFieldValues: Yup.array()
                        .notRequired()
                        .of(
                          Yup.string().required(
                            "Clear Member Name Field Values is required"
                          )
                        ),
                    }),
                  }),
                LevenshteinDistanceScores: Yup.array()
                  .notRequired()
                  .of(
                    Yup.object().shape({
                      key: Yup.string().required("Key is required"),
                      value: Yup.string().required("Value is required"),
                    })
                  ),
                ClearMemberNameFieldValues: Yup.array()
                  .notRequired()
                  .of(
                    Yup.string().required(
                      "Clear Member Name Field Values is required"
                    )
                  ),
                ExactMatchScore: Yup.number().required(
                  "Exact Match Score is required"
                ),
              }),
            })
          ),
        MinimumMatchingScoreToQualifyAsMatch: Yup.number()
          .notRequired()
          .test(
            "minimum-matching-score-required-if",
            "Minimum Matching Score to Qualify as Match is required",
            (value, context) => {
              if (context.from) {
                let values = context.from[context.from?.length - 1].value;
                if (values.useFuzzyMemberMatching) {
                  return value != null;
                }
              }
              return true;
            }
          ),
      }),
    geoSuggestCountryFilter: Yup.array()
      .of(Yup.string())
      .test("max-length", "You can select up to 5 countries.", (value) => {
        return !value || value.length <= 5;
      }),
    customerRiskManagementFlowConfiguration: Yup.object()
      .nullable()
      .notRequired()
      .test(
        "bndry-job-id-required",
        "Onboard Individual Entity Job Id is required when Risk Management Provider is Bndry.",
        function (
          value:
            | CherryHubAdminApi.CustomerRiskManagementFlowConfiguration
            | null
            | undefined
        ) {
          if (!this.parent.customerRiskManagementFlowEnabled) {
            return true;
          }
          if (value?.ThirdParty !== CustomerRiskManagementThirdParty.Bndry) {
            return true;
          }
          const jobId =
            value.BndryCustomerThirdPartyWorkflowConfiguration
              ?.OnboardIndividualEntityJobId;
          if (!!jobId && jobId.trim() !== "") {
            return true;
          }
          return this.createError({
            path: "customerRiskManagementFlowConfiguration.BndryCustomerThirdPartyWorkflowConfiguration.OnboardIndividualEntityJobId",
            message:
              "Onboard Individual Entity Job Id is required when Risk Management Provider is Bndry.",
          });
        }
      )
      .test(
        "au10tix-workflow-type-required",
        "Workflow Type is required when Risk Management Provider is Au10Tix.",
        function (
          value:
            | CherryHubAdminApi.CustomerRiskManagementFlowConfiguration
            | null
            | undefined
        ) {
          if (!this.parent.customerRiskManagementFlowEnabled) {
            return true;
          }
          if (value?.ThirdParty !== CustomerRiskManagementThirdParty.Au10Tix) {
            return true;
          }
          const workflowType =
            value.Au10TixCustomerThirdPartyWorkflowConfiguration?.WorkflowType;
          if (!!workflowType && workflowType.trim() !== "") {
            return true;
          }
          return this.createError({
            path: "customerRiskManagementFlowConfiguration.Au10TixCustomerThirdPartyWorkflowConfiguration.WorkflowType",
            message:
              "Workflow Type is required when Risk Management Provider is Au10Tix.",
          });
        }
      ),
  }),
})(InnerForm);

interface NotificationFieldProps {
  headerLabel: string;
  fieldName: string;
  configuration: CherryHubAdminApi.SendNotificationConfiguration | undefined;
  secondProps?: BoxProps;
  templateOptions: CherryHubAdminApi.IdNameSet[] | null;
  templateOptionsLoading: boolean;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

const NotificationField = ({
  headerLabel,
  fieldName,
  secondProps,
  configuration,
  templateOptions,
  templateOptionsLoading,
  setFieldValue,
  ...props
}: NotificationFieldProps & BoxProps) => {
  const dep = JSON.stringify(configuration);

  const view = useMemo(() => {
    return (
      <Box
        w="100%"
        p={4}
        border="1px solid"
        borderColor="cherryButtonDark.50"
        borderRadius="md"
        {...props}
      >
        <Text fontSize="sm">
          <b>{headerLabel}</b>
        </Text>
        <Divider mb={2} />

        <VStack w="100%" alignItems="flex-start">
          <TextField
            horizontal
            label="Sender Name"
            name={`${fieldName}.SenderName`}
          />

          {/* <TextField horizontal
            label="Sender Email Address"
            name={`${fieldName}.SenderEmailAddress`}
          /> */}

          <ChakraReactSelectField
            horizontal
            label="Email Template"
            name={`${fieldName}.TemplateId`}
            selectedOptionColor="blue"
            isLoading={templateOptionsLoading}
            options={
              templateOptions?.map((provider) => {
                return {
                  label: provider.Name!,
                  value: provider.Id!,
                };
              }) || []
            }
          />

          <Box
            w="100%"
            p={4}
            border="1px solid"
            borderColor="cherryButtonDark.100"
            borderRadius="md"
            {...secondProps}
          >
            <HStack w="100%">
              <Text mr="auto" fontSize="sm">
                <b>Recipient Email Addresses</b>
              </Text>
              <Button
                ml="auto"
                mr="0"
                size="xs"
                leftIcon={<PlusIcon />}
                onClick={() => {
                  if (configuration?.RecipientEmailAddresses == null) {
                    setFieldValue(`${fieldName}.RecipientEmailAddresses`, [""]);
                  } else {
                    configuration?.RecipientEmailAddresses?.push("");
                    setFieldValue(
                      `${fieldName}.RecipientEmailAddresses`,
                      configuration?.RecipientEmailAddresses
                    );
                  }
                }}
                colorScheme="cherryButton"
              >
                New Email Address
              </Button>
            </HStack>
            <Divider mb={2} mt={1} />
            <VStack w="100%">
              {(configuration?.RecipientEmailAddresses?.length &&
                configuration?.RecipientEmailAddresses?.map(
                  (email, emailIdx) => {
                    return (
                      <HStack
                        w="100%"
                        alignItems="flex-start"
                        key={emailIdx}
                        spacing={2}
                      >
                        <TextField
                          horizontal
                          label={`Recipient Email Addresses ${emailIdx + 1}`}
                          name={`${fieldName}.RecipientEmailAddresses[${emailIdx}]`}
                        />
                        {(configuration?.RecipientEmailAddresses?.length ?? 0) >
                          1 && (
                          <Box h="100%" pb={1} alignSelf="flex-end">
                            <IconButton
                              aria-label="Delete recipient email address."
                              title="Delete recipient email address."
                              icon={<DeleteIcon />}
                              size="sm"
                              onClick={() => {
                                configuration?.RecipientEmailAddresses?.splice(
                                  emailIdx,
                                  1
                                );
                                setFieldValue(
                                  `${fieldName}.RecipientEmailAddresses`,
                                  configuration?.RecipientEmailAddresses
                                );
                              }}
                            />
                          </Box>
                        )}
                      </HStack>
                    );
                  }
                )) || (
                <Text fontStyle="italic" fontSize="sm">
                  Recipient Email Addresses has not been set.
                </Text>
              )}
            </VStack>
          </Box>
        </VStack>
      </Box>
    );
  }, [dep, templateOptions, templateOptionsLoading]);
  return <>{view}</>;
};

interface OTPSectionProps {
  oTPFlowRequired: boolean | undefined;
  oTPValidForMinutes: number | undefined;
  oTPValidTimePeriod: CherryHubAdminApi.TimePeriod | undefined;
}

const OTPSection = ({
  oTPFlowRequired,
  oTPValidForMinutes,
  oTPValidTimePeriod,
}: OTPSectionProps) => {
  const view = useMemo(() => {
    return (
      <AccordionItem>
        <h2>
          <AccordionButton
            _expanded={{
              bg: "cherryButtonDark.50",
              color: "white",
            }}
          >
            <Box flex="1" textAlign="left">
              <b>OTP</b>
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel width="100%">
          <VStack w="100%" alignItems="flex-start">
            <SwitchField
              horizontal
              label="OTP Flow Required"
              name="oTPFlowRequired"
            />

            {oTPFlowRequired && (
              <>
                <TimePeriod
                  headerLabel="OTP Valid Time Period"
                  fieldName="oTPValidTimePeriod"
                  timePeriod={oTPValidTimePeriod}
                  isRelativeListbox={true}
                />

                <IntegerField
                  horizontal
                  label="OTP Valid For Minutes"
                  name="oTPValidForMinutes"
                />
              </>
            )}
          </VStack>
        </AccordionPanel>
      </AccordionItem>
    );
  }, [oTPFlowRequired, oTPValidForMinutes, oTPValidTimePeriod]);

  return <>{view}</>;
};

interface PaymentSectionProps {
  paymentRequired: boolean | undefined;
  paymentConfiguration: CherryHubAdminApi.PaymentConfiguration | undefined;
}

const PaymentSection = ({
  paymentRequired,
  paymentConfiguration,
}: PaymentSectionProps) => {
  const paymentConfigurationView = useMemo(() => {
    return (
      <Box
        w="100%"
        p={4}
        border="1px solid"
        borderColor="cherryButtonDark.50"
        borderRadius="md"
      >
        <Text fontSize="sm">
          <b>Payment Configuration</b> :
        </Text>
        <Divider mb={2} />

        <VStack w="100%" alignItems="flex-start">
          <ChakraReactSelectField
            horizontal
            label="Payment Provider"
            name="paymentConfiguration.PaymentProvider"
            selectedOptionColor="blue"
            options={PaymentProviderList()}
            className="relative-listbox"
          />

          {paymentConfiguration?.PaymentProvider == PaymentProvider.Stripe && (
            <Box
              w="100%"
              p={4}
              border="1px solid"
              borderColor="cherryButtonDark.100"
              borderRadius="md"
            >
              <Text fontSize="sm">
                <b>Stripe Configuration</b>
              </Text>
              <Divider mb={2} />
              <VStack w="100%">
                <TextField
                  horizontal
                  label="Account Id"
                  name="paymentConfiguration.StripeConfiguration.AccountId"
                />

                <SwitchField
                  horizontal
                  label="Use On Behalf Of Account Id"
                  name="paymentConfiguration.StripeConfiguration.UseOnBehalfOfAccountId"
                />

                <SwitchField
                  horizontal
                  label="Use Automatic Payment Method"
                  name="paymentConfiguration.StripeConfiguration.UseAutomaticPaymentMethod"
                />

                <ChakraReactSelectField
                  horizontal
                  label="ISO Currency Code"
                  name="paymentConfiguration.StripeConfiguration.ISOCurrencyCode"
                  selectedOptionColor="blue"
                  options={ISOCurrencyCodeList()}
                />

                <TextArea
                  horizontal
                  label="Order Description"
                  name="paymentConfiguration.StripeConfiguration.OrderDescription"
                />
              </VStack>
            </Box>
          )}

          {paymentConfiguration?.PaymentProvider == PaymentProvider.Payrix && (
            <Box
              w="100%"
              p={4}
              border="1px solid"
              borderColor="cherryButtonDark.100"
              borderRadius="md"
            >
              <Text fontSize="sm">
                <b>Payrix Configuration</b>
              </Text>
              <Divider mb={2} />
              <VStack w="100%">
                <TextField
                  horizontal
                  label="Account Id"
                  name="paymentConfiguration.PayrixConfiguration.AccountId"
                />

                <SwitchField
                  horizontal
                  label="Use Automatic Payment Method"
                  name="paymentConfiguration.PayrixConfiguration.UseAutomaticPaymentMethod"
                />

                <TextArea
                  horizontal
                  label="Order Description"
                  name="paymentConfiguration.PayrixConfiguration.OrderDescription"
                />
              </VStack>
            </Box>
          )}

          {paymentConfiguration?.PaymentProvider ==
            PaymentProvider.Worldpay && (
            <Box
              w="100%"
              p={4}
              border="1px solid"
              borderColor="cherryButtonDark.100"
              borderRadius="md"
            >
              <Text fontSize="sm">
                <b>Worldpay Configuration</b>
              </Text>
              <Divider mb={2} />
              <VStack w="100%">
                <TextField
                  horizontal
                  label="Account Id"
                  name="paymentConfiguration.WorldpayConfiguration.AccountId"
                />

                <TextArea
                  horizontal
                  label="Order Description"
                  name="paymentConfiguration.WorldpayConfiguration.OrderDescription"
                />

                <TextField
                  horizontal
                  label="Template Id"
                  name="paymentConfiguration.WorldpayConfiguration.TemplateId"
                />
              </VStack>
            </Box>
          )}
        </VStack>
      </Box>
    );
  }, [paymentConfiguration]);

  const paymentRequiredView = useMemo(() => {
    return (
      <AccordionItem>
        {({ isExpanded }) => (
          <>
            <h2>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <b>Payment</b>
                </Box>
                {isExpanded ? (
                  <ChevronUpIcon color="white" fontSize="xl" />
                ) : (
                  <AccordionIcon />
                )}
              </AccordionButton>
            </h2>
            <AccordionPanel width="100%">
              <VStack w="100%" alignItems="flex-start">
                <SwitchField
                  horizontal
                  label="Payment Required"
                  name="paymentRequired"
                />
                {paymentRequired && paymentConfigurationView}
              </VStack>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    );
  }, [paymentRequired, paymentConfiguration]);

  return <>{paymentRequiredView}</>;
};

interface TwoStepFlowSectionProps {
  twoStepFlowRequired: boolean | undefined;
  twoStepFlowConfiguration:
    | CherryHubAdminApi.TwoStepFlowConfiguration
    | undefined;
  digitalMembershipCardOptions: { label: string; value: string }[];
  sendMembershipSubmissionNotificationEmail: boolean | undefined;
  submissionNotificationEmailConfiguration:
    | CherryHubAdminApi.SendNotificationConfiguration
    | undefined;
  templateOptions: CherryHubAdminApi.IdNameSet[] | null;
  templateOptionsLoading: boolean;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

const TwoStepFlowSection = ({
  twoStepFlowRequired,
  twoStepFlowConfiguration,
  digitalMembershipCardOptions,
  sendMembershipSubmissionNotificationEmail,
  submissionNotificationEmailConfiguration,
  templateOptions,
  templateOptionsLoading,
  setFieldValue,
}: TwoStepFlowSectionProps) => {
  const submissionDep = JSON.stringify(
    submissionNotificationEmailConfiguration
  );

  const submissionNotifEmailConfView = useMemo(() => {
    return (
      <NotificationField
        headerLabel="Membership Application Notification Email"
        fieldName="submissionNotificationEmailConfiguration"
        configuration={submissionNotificationEmailConfiguration}
        templateOptions={templateOptions}
        templateOptionsLoading={templateOptionsLoading}
        setFieldValue={setFieldValue}
      />
    );
  }, [submissionDep, templateOptions, templateOptionsLoading]);

  const twoStepFlowConfigurationView = useMemo(() => {
    return (
      <Box
        w="100%"
        p={4}
        border="1px solid"
        borderColor="cherryButtonDark.50"
        borderRadius="md"
      >
        <Text fontSize="sm">
          <b>Two Step Flow Configuration</b> :
        </Text>
        <Divider mb={2} />

        <VStack w="100%" alignItems="flex-start">
          <TextArea
            horizontal
            label="Approved Member Submission Text"
            name="twoStepFlowConfiguration.ApprovedMemberSubmissionText"
          />

          <TextArea
            horizontal
            label="Rejected Member Submission Text"
            name="twoStepFlowConfiguration.RejectedMemberSubmissionText"
          />

          <TextArea
            horizontal
            label="Approved Member Pass Update Message"
            name="twoStepFlowConfiguration.ApprovedMemberPassUpdateMessage"
          />

          <TextArea
            horizontal
            label="Rejected Member Pass Update Message"
            name="twoStepFlowConfiguration.RejectedMemberPassUpdateMessage"
          />

          <TimePeriod
            headerLabel="Membership Request Valid For"
            fieldName="twoStepFlowConfiguration.MembershipRequestValidFor"
            timePeriod={twoStepFlowConfiguration?.MembershipRequestValidFor}
            quantityValueProps={{ flex: 1 }}
            periodValueProps={{ flex: 1 }}
          />

          <ChakraReactSelectField
            horizontal
            label="Membership Application Pass"
            name="twoStepFlowConfiguration.PassConfigurationId"
            selectedOptionColor="blue"
            options={digitalMembershipCardOptions}
            className="relative-listbox"
          />

          <Box
            w="100%"
            p={4}
            border="1px solid"
            borderColor="cherryButtonDark.100"
            borderRadius="md"
          >
            <Text fontSize="sm">
              <b>Membership Application Pass Download Email</b> :
            </Text>
            <Divider mb={2} />

            <VStack w="100%" alignItems="flex-start">
              {/* <IntegerField horizontal
                label="Version"
                name="twoStepFlowConfiguration.PassInvitationHostConfiguration.Version"
              />

              <TextField horizontal
                label="Landing Page Url Format"
                name="twoStepFlowConfiguration.PassInvitationHostConfiguration.LandingPageUrlFormat"
              />

              <SwitchField horizontal
                label="Landing Page Set Prize Flag"
                name="twoStepFlowConfiguration.PassInvitationHostConfiguration.LandingPageSetPrizeFlag"
              />

              <TextField horizontal
                label="Landing Page Error Url Format"
                name="twoStepFlowConfiguration.PassInvitationHostConfiguration.LandingPageErrorUrlFormat"
              />

              <TextField horizontal
                label="Download From App Url Format"
                name="twoStepFlowConfiguration.PassInvitationHostConfiguration.DownloadFromAppUrlFormat"
              />

              <TextField horizontal
                label="Download From Invitation Url Format"
                name="twoStepFlowConfiguration.PassInvitationHostConfiguration.DownloadFromInvitationUrlFormat"
              /> */}

              <ChakraReactSelectField
                horizontal
                label="Email Template"
                name="twoStepFlowConfiguration.PassInvitationHostConfiguration.EmailTemplateId"
                selectedOptionColor="blue"
                isLoading={templateOptionsLoading}
                options={
                  templateOptions?.map((provider) => {
                    return {
                      label: provider.Name!,
                      value: provider.Id!,
                    };
                  }) || []
                }
              />

              <TextField
                horizontal
                label="Email Sender Name"
                name="twoStepFlowConfiguration.PassInvitationHostConfiguration.EmailSenderName"
              />

              {/* <TextField horizontal
                label="Email Sender Email Address"
                name="twoStepFlowConfiguration.PassInvitationHostConfiguration.EmailSenderEmailAddress"
              /> */}
            </VStack>
          </Box>
        </VStack>
      </Box>
    );
  }, [
    twoStepFlowConfiguration,
    digitalMembershipCardOptions,
    templateOptions,
    templateOptionsLoading,
  ]);

  const twoStepFlowView = useMemo(() => {
    return (
      <AccordionItem>
        {({ isExpanded }) => (
          <>
            <h2>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <b>Two Step Flow</b>
                </Box>
                {isExpanded ? (
                  <ChevronUpIcon color="white" fontSize="xl" />
                ) : (
                  <AccordionIcon />
                )}
              </AccordionButton>
            </h2>
            <AccordionPanel width="100%">
              <VStack w="100%" alignItems="flex-start">
                <SwitchField
                  horizontal
                  label="Two Step Flow Required"
                  name="twoStepFlowRequired"
                />
                {twoStepFlowRequired && (
                  <>
                    {twoStepFlowConfigurationView}

                    <SwitchField
                      horizontal
                      label="Send Notification Email to the Venue"
                      name="sendMembershipSubmissionNotificationEmail"
                    />

                    {sendMembershipSubmissionNotificationEmail &&
                      submissionNotifEmailConfView}
                  </>
                )}
              </VStack>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    );
  }, [
    twoStepFlowRequired,
    sendMembershipSubmissionNotificationEmail,
    submissionDep,
    templateOptions,
    templateOptionsLoading,
  ]);

  return <>{twoStepFlowView}</>;
};

interface AdhocPassInvitationSectionProps {
  adhocPassInvitationConfiguration:
    | CherryHubAdminApi.AdhocPassInvitationConfiguration
    | undefined;
}

const AdhocPassInvitationSection = ({
  adhocPassInvitationConfiguration,
}: AdhocPassInvitationSectionProps) => {
  const view = useMemo(() => {
    return (
      <AccordionItem>
        {({ isExpanded }) => (
          <>
            <h2>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <b>Adhoc Pass Invitation Configuration</b>
                </Box>
                {isExpanded ? (
                  <ChevronUpIcon color="white" fontSize="xl" />
                ) : (
                  <AccordionIcon />
                )}
              </AccordionButton>
            </h2>
            <AccordionPanel width="100%">
              <VStack w="100%" alignItems="flex-start">
                <TextField
                  horizontal
                  label="First Device Registration Pass Update Message"
                  name="adhocPassInvitationConfiguration.FirstDeviceRegistrationPassUpdateMessage"
                />

                <TimePeriod
                  headerLabel="First Device Registration Pass Update Message Delay"
                  fieldName={`adhocPassInvitationConfiguration.FirstDeviceRegistrationPassUpdateMessageDelay`}
                  timePeriod={
                    adhocPassInvitationConfiguration?.FirstDeviceRegistrationPassUpdateMessageDelay
                  }
                  isRelativeListbox={true}
                  quantityValueProps={{ flex: 1 }}
                  periodValueProps={{ flex: 1 }}
                />
              </VStack>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    );
  }, [adhocPassInvitationConfiguration]);

  return <>{view}</>;
};

interface FieldMatchingConfigurationSectionProps {
  fieldMatchingConfiguration: FieldMatchingConfiguration;
  matchableMemberFieldOptions: CherryHubAdminApi.EnumConfigurationItem[] | null;
  fieldName: string;
  isLoading: boolean;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  errors: FormikErrors<RegistrationFormConfigurationDetailFormValues>;
  isFallback: boolean;
}

const FieldMatchingConfigurationSection = ({
  fieldMatchingConfiguration,
  matchableMemberFieldOptions,
  fieldName,
  isLoading,
  setFieldValue,
  errors,
  isFallback,
}: FieldMatchingConfigurationSectionProps) => {
  const dep = JSON.stringify(fieldMatchingConfiguration);

  const view = useMemo(() => {
    return (
      <VStack w="100%" spacing={4} alignItems="flex-start">
        <ChakraReactSelectField
          horizontal
          valueProps={{ flex: 2 }}
          label="Alternate Member Name Match Field"
          name={`${fieldName}.AlternateMemberNameMatchField`}
          options={
            matchableMemberFieldOptions?.map((provider) => {
              return {
                label: provider.Value!,
                value: provider.Key!,
              };
            }) || []
          }
          selectedOptionColor="blue"
          isLoading={isLoading}
        />
        <IntegerField
          horizontal
          valueProps={{ flex: 2 }}
          label="Exact Match Score"
          name={`${fieldName}.ExactMatchScore`}
        />

        <IntegerField
          horizontal
          valueProps={{ flex: 2 }}
          label="Name Diminuitive Score"
          name={`${fieldName}.NameDiminuitiveScore`}
        />

        <KeyValuePairField
          headerLabel="Levenshtein Distance Scores"
          fieldName={`${fieldName}.LevenshteinDistanceScores`}
          keyValuePairs={fieldMatchingConfiguration.LevenshteinDistanceScores}
          setFieldValue={setFieldValue}
          useNumericFieldKey={true}
          useNumericFieldValue={true}
          borderColor="cherryButtonDark.100"
        />

        <IntegerField
          horizontal
          valueProps={{ flex: 2 }}
          label="Name Starts with Percentage of Length"
          name={`${fieldName}.NameStartsWithPercentageOfLength`}
        />

        <IntegerField
          horizontal
          valueProps={{ flex: 2 }}
          label="Name Starts with Score"
          name={`${fieldName}.NameStartsWithScore`}
        />

        <Box
          w="100%"
          p={4}
          border="1px solid"
          borderColor="cherryButtonDark.100"
          borderRadius="md"
        >
          <HStack w="100%">
            <Text mr="auto" fontSize="sm">
              <b>Clear Member Name Field Values</b>
            </Text>

            <Button
              ml="auto"
              mr="0"
              size="xs"
              leftIcon={<PlusIcon />}
              onClick={() => {
                if (
                  fieldMatchingConfiguration.ClearMemberNameFieldValues == null
                ) {
                  setFieldValue(`${fieldName}.ClearMemberNameFieldValues`, [
                    "",
                  ]);
                } else {
                  fieldMatchingConfiguration.ClearMemberNameFieldValues?.push(
                    ""
                  );
                  setFieldValue(
                    `${fieldName}.ClearMemberNameFieldValues`,
                    fieldMatchingConfiguration.ClearMemberNameFieldValues
                  );
                }
              }}
              colorScheme="cherryButton"
            >
              New Clear Member Name Field Values
            </Button>
          </HStack>
          <Divider mb={2} mt={1} />
          <VStack w="100%">
            {(fieldMatchingConfiguration.ClearMemberNameFieldValues?.length &&
              fieldMatchingConfiguration.ClearMemberNameFieldValues?.map(
                (clearMemberNameFieldValue, clearMemberNameFieldValueIdx) => (
                  <HStack
                    w="100%"
                    alignItems="flex-start"
                    key={clearMemberNameFieldValueIdx}
                    spacing={2}
                  >
                    <TextField
                      horizontal
                      label={`Clear Member Name Field Values ${
                        clearMemberNameFieldValueIdx + 1
                      }`}
                      name={`${fieldName}.ClearMemberNameFieldValues[${clearMemberNameFieldValueIdx}]`}
                    />

                    {(fieldMatchingConfiguration.ClearMemberNameFieldValues
                      ?.length ?? 0) > 0 && (
                      <Box h="100%" pb={1} alignSelf="flex-end">
                        <IconButton
                          aria-label="Delete clear member name field values."
                          title="Delete clear member name field values."
                          icon={<DeleteIcon />}
                          size="sm"
                          onClick={() => {
                            fieldMatchingConfiguration.ClearMemberNameFieldValues?.splice(
                              clearMemberNameFieldValueIdx,
                              1
                            );
                            setFieldValue(
                              `${fieldName}.ClearMemberNameFieldValues`,
                              fieldMatchingConfiguration.ClearMemberNameFieldValues
                            );
                          }}
                        />
                      </Box>
                    )}
                  </HStack>
                )
              )) || (
              <Text fontStyle="italic" fontSize="sm">
                Clear Member Name Field Values has not been set.
              </Text>
            )}
          </VStack>
        </Box>

        {!isFallback && (
          <>
            <IntegerField
              horizontal
              valueProps={{ flex: 2 }}
              label="Test Fallback Match Field When Score Less Than"
              name={`${fieldName}.TestFallbackMatchFieldWhenScoreLessThan`}
            />
            <Box
              w="100%"
              p={4}
              border="1px solid"
              borderColor="cherryButtonDark.50"
              borderRadius="md"
            >
              <HStack w="100%">
                <Text flexGrow={2} fontSize="sm">
                  <b>Fallback Match Field</b> :
                </Text>
                {fieldMatchingConfiguration.FallbackMatchField == null && (
                  <Button
                    ml="auto"
                    mr="0"
                    size="xs"
                    leftIcon={<PlusIcon />}
                    onClick={() => {
                      setFieldValue(`${fieldName}.FallbackMatchField`, {
                        key: "",
                        value: "",
                      });
                    }}
                    colorScheme="cherryButton"
                  >
                    New Fallback Match Field
                  </Button>
                )}
              </HStack>
              <Divider mb={2} mt={1} />

              <VStack w="100%">
                {(fieldMatchingConfiguration.FallbackMatchField && (
                  <HStack w="100%">
                    <VStack w="100%">
                      <ChakraReactSelectField
                        horizontal
                        valueProps={{ flex: 2 }}
                        className="relative-listbox"
                        label={`Fallback Match Field Key`}
                        name={`${fieldName}.FallbackMatchField.key`}
                        options={
                          matchableMemberFieldOptions?.map((provider) => {
                            return {
                              label: provider.Value!,
                              value: provider.Key!,
                            };
                          }) || []
                        }
                        selectedOptionColor="blue"
                        isLoading={isLoading}
                      />
                      <FieldMatchingConfigurationSection
                        errors={errors}
                        fieldMatchingConfiguration={
                          fieldMatchingConfiguration.FallbackMatchField?.value
                        }
                        fieldName={`${fieldName}.FallbackMatchField.value`}
                        isFallback={true}
                        isLoading={isLoading}
                        matchableMemberFieldOptions={
                          matchableMemberFieldOptions
                        }
                        setFieldValue={setFieldValue}
                      />
                    </VStack>
                    <Box h="100%" pb={1} alignSelf="flex-end">
                      <IconButton
                        aria-label="Delete Fallback Match Field."
                        title="Delete Fallback Match Field."
                        icon={<DeleteIcon />}
                        size="sm"
                        onClick={() => {
                          fieldMatchingConfiguration.FallbackMatchField = null;
                          setFieldValue(
                            `${fieldName}.FallbackMatchField`,
                            fieldMatchingConfiguration.FallbackMatchField
                          );
                        }}
                      />
                    </Box>
                  </HStack>
                )) || (
                  <Text fontStyle="italic" fontSize="sm">
                    Clear Fallback Match Field has not been set.
                  </Text>
                )}
              </VStack>
            </Box>
          </>
        )}
      </VStack>
    );
  }, [dep, isLoading, errors]);
  return <>{view}</>;
};

interface MemberMatchingConfigurationSectionProps {
  memberMatchingConfiguration: MemberMatchingConfiguration | null | undefined;
  matchableMemberFieldOptions: CherryHubAdminApi.EnumConfigurationItem[] | null;
  isLoading: boolean;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  errors: FormikErrors<RegistrationFormConfigurationDetailFormValues>;
  addFieldMatchingConfiguration: () => void;
}

const MemberMatchingConfigurationSection = ({
  memberMatchingConfiguration,
  matchableMemberFieldOptions,
  isLoading,
  setFieldValue,
  errors,
  addFieldMatchingConfiguration,
}: MemberMatchingConfigurationSectionProps) => {
  const dep = JSON.stringify(
    memberMatchingConfiguration?.FieldMatchingConfigurations
  );

  const fieldMatchingConfigurations = useMemo(() => {
    const hasConfigurations =
      (memberMatchingConfiguration?.FieldMatchingConfigurations &&
        memberMatchingConfiguration?.FieldMatchingConfigurations.length > 0) ??
      false;

    let i = 0;
    let fieldConfigurations: JSX.Element | JSX.Element[];
    if (hasConfigurations) {
      fieldConfigurations =
        memberMatchingConfiguration!.FieldMatchingConfigurations!.map(
          (fieldMatchingConfiguration, index) => {
            return (
              <AccordionItem w="100%" key={index}>
                <h2>
                  <AccordionButton textAlign="left" gap={4}>
                    <Box flexShrink={1}>
                      <b>{`Member Matching Configuration #${
                        fieldMatchingConfiguration.key ?? index + 1
                      }`}</b>
                    </Box>
                    <Box flexGrow={1}>
                      <IconButton
                        aria-label="remove-field-matching"
                        title="Remove Field Matching Configuration"
                        icon={<TrashIcon />}
                        size="xs"
                        bgColor="cherryGrey.160"
                        onClick={() => {
                          memberMatchingConfiguration?.FieldMatchingConfigurations?.splice(
                            index,
                            1
                          );
                          setFieldValue(
                            "memberMatchingConfiguration.FieldMatchingConfigurations",
                            memberMatchingConfiguration?.FieldMatchingConfigurations
                          );
                        }}
                      />
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel width="100%">
                  <VStack w="100%" spacing={4} alignItems="flex-start">
                    <ChakraReactSelectField
                      horizontal
                      valueProps={{ flex: 2 }}
                      label="Field Matching Configuration Key"
                      name={`memberMatchingConfiguration.FieldMatchingConfigurations[${index}].key`}
                      options={
                        matchableMemberFieldOptions?.map((provider) => {
                          return {
                            label: provider.Value!,
                            value: provider.Key!,
                          };
                        }) || []
                      }
                      selectedOptionColor="blue"
                      isLoading={isLoading}
                    />
                    <FieldMatchingConfigurationSection
                      fieldMatchingConfiguration={
                        fieldMatchingConfiguration.value
                      }
                      fieldName={`memberMatchingConfiguration.FieldMatchingConfigurations[${index}].value`}
                      matchableMemberFieldOptions={matchableMemberFieldOptions}
                      isLoading={isLoading}
                      setFieldValue={setFieldValue}
                      errors={errors}
                      isFallback={false}
                    />
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            );
          }
        );
    } else {
      fieldConfigurations = (
        <VStack w="100%" p={4} spacing={2}>
          <i>Field Matching Configuration has not set</i>
          <Button
            size="sm"
            leftIcon={<PlusIcon />}
            onClick={addFieldMatchingConfiguration}
            colorScheme="cherryButton"
          >
            Add Field Matching Configuration
          </Button>
        </VStack>
      );
    }

    return (
      <Accordion w="100%" allowMultiple allowToggle variant="custom-field">
        <VStack w="100%" spacing={4}>
          {fieldConfigurations}
        </VStack>
      </Accordion>
    );
  }, [dep, isLoading, errors]);

  const view = useMemo(() => {
    return (
      <AccordionItem>
        {({ isExpanded }) => (
          <>
            <h2>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <b>Member Matching Configuration</b>
                </Box>
                {isExpanded ? (
                  <ChevronUpIcon color="white" fontSize="xl" />
                ) : (
                  <AccordionIcon />
                )}
              </AccordionButton>
            </h2>
            <AccordionPanel width="100%">
              <VStack w="100%" alignItems="flex-start">
                <IntegerField
                  horizontal
                  label="Minimum Matching Score to Qualify as Match"
                  name={`memberMatchingConfiguration.MinimumMatchingScoreToQualifyAsMatch`}
                />
                {memberMatchingConfiguration?.FieldMatchingConfigurations &&
                  Object.keys(
                    memberMatchingConfiguration?.FieldMatchingConfigurations
                  ).length > 0 && (
                    <Button
                      leftIcon={<PlusIcon />}
                      onClick={addFieldMatchingConfiguration}
                      colorScheme="cherryButton"
                      alignSelf="flex-end"
                      size="sm"
                    >
                      Add Field Matching Configuration
                    </Button>
                  )}

                {fieldMatchingConfigurations}
              </VStack>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    );
  }, [
    dep,
    isLoading,
    memberMatchingConfiguration?.MinimumMatchingScoreToQualifyAsMatch,
    errors,
  ]);

  return <>{view}</>;
};

interface NotificationSectionProps {
  notificationEmailConfiguration:
    | CherryHubAdminApi.SendNotificationConfiguration
    | undefined;
  sendNewMemberNotificationEmail: boolean | undefined;
  sendMemberReceiptEmail: boolean | undefined;
  sendMemberReceiptEmailAfterMembershipCreation: boolean | undefined;
  memberReceiptEmailConfiguration:
    | CherryHubAdminApi.SendNotificationConfiguration
    | undefined;
  templateOptions: CherryHubAdminApi.IdNameSet[] | null;
  templateOptionsLoading: boolean;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

const NotificationSection = ({
  notificationEmailConfiguration,
  sendNewMemberNotificationEmail,
  sendMemberReceiptEmail,
  sendMemberReceiptEmailAfterMembershipCreation,
  memberReceiptEmailConfiguration,
  templateOptions,
  templateOptionsLoading,
  setFieldValue,
}: NotificationSectionProps) => {
  const notifDep = JSON.stringify(notificationEmailConfiguration);
  const memberReceiptDep = JSON.stringify(memberReceiptEmailConfiguration);

  const memberReceiptEmailConfView = useMemo(() => {
    return (
      <NotificationField
        headerLabel="Member Receipt Email Configuration"
        fieldName="memberReceiptEmailConfiguration"
        configuration={memberReceiptEmailConfiguration}
        templateOptions={templateOptions}
        templateOptionsLoading={templateOptionsLoading}
        setFieldValue={setFieldValue}
      />
    );
  }, [memberReceiptDep, templateOptions, templateOptionsLoading]);

  const view = useMemo(() => {
    return (
      <AccordionItem>
        {({ isExpanded }) => (
          <>
            <h2>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <b>Notification</b>
                </Box>
                {isExpanded ? (
                  <ChevronUpIcon color="white" fontSize="xl" />
                ) : (
                  <AccordionIcon />
                )}
              </AccordionButton>
            </h2>
            <AccordionPanel width="100%">
              <VStack w="100%" alignItems="flex-start">
                <SwitchField
                  horizontal
                  label="Send New Member Notification Email to the Venue"
                  name="sendNewMemberNotificationEmail"
                />

                {sendNewMemberNotificationEmail && (
                  <NotificationField
                    headerLabel="Notification Email Configuration"
                    fieldName="notificationEmailConfiguration"
                    configuration={notificationEmailConfiguration}
                    templateOptions={templateOptions}
                    templateOptionsLoading={templateOptionsLoading}
                    setFieldValue={setFieldValue}
                  />
                )}

                <SwitchField
                  horizontal
                  label="Send Receipt Email to the Member"
                  name="sendMemberReceiptEmail"
                  onChange={(event) => {
                    setFieldValue(
                      "sendMemberReceiptEmail",
                      event.target.checked
                    );
                    if (!event.target.checked) {
                      setFieldValue(
                        "sendMemberReceiptEmailAfterMembershipCreation",
                        false
                      );
                    }
                  }}
                />

                <SwitchField
                  horizontal
                  label="Send Member Receipt Email After Membership Creation"
                  name="sendMemberReceiptEmailAfterMembershipCreation"
                  isDisabled={!sendMemberReceiptEmail}
                />

                {sendMemberReceiptEmail && memberReceiptEmailConfView}
              </VStack>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    );
  }, [
    notifDep,
    sendNewMemberNotificationEmail,
    sendMemberReceiptEmail,
    sendMemberReceiptEmailAfterMembershipCreation,
    memberReceiptDep,
    templateOptions,
    templateOptionsLoading,
  ]);

  return <>{view}</>;
};

interface HiddenMemberFieldSectionProps {
  hiddenMemberFields: string[] | null | undefined;
  memberFieldOptions: CherryHubAdminApi.EnumConfigurationItem[] | null;
  memberFieldLoading: boolean;
}

const HiddenMemberFieldSection = ({
  hiddenMemberFields,
  memberFieldOptions,
  memberFieldLoading,
}: HiddenMemberFieldSectionProps) => {
  const view = useMemo(() => {
    return (
      <ChakraReactSelectField
        horizontal
        isMulti
        label="Hidden Member Fields"
        name="hiddenMemberFields"
        selectedOptionColor="blue"
        closeMenuOnSelect={false}
        isLoading={memberFieldLoading}
        options={
          memberFieldOptions?.map((memberField) => {
            return {
              label: memberField.Value!,
              value: memberField.Key!,
            };
          }) || []
        }
        valueProps={{ mb: 4 }}
      />
    );
  }, [hiddenMemberFields, memberFieldOptions, memberFieldLoading]);

  return <>{view}</>;
};

interface ThemeDataSectionProps {
  themeData: unknown | null;
  errors: string | undefined;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

const ThemeDataSection = ({
  themeData,
  errors,
  setFieldValue,
}: ThemeDataSectionProps) => {
  const view = useMemo(() => {
    return (
      <AccordionItem>
        {({ isExpanded }) => (
          <>
            <h2>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <b>Theme Data</b>
                </Box>
                {isExpanded ? (
                  <ChevronUpIcon color="white" fontSize="xl" />
                ) : (
                  <AccordionIcon />
                )}
              </AccordionButton>
            </h2>

            <AccordionPanel width="100%">
              <VStack w="100%" alignItems="flex-start">
                <JsonTextAreaField
                  horizontal
                  label="Theme Data"
                  name="themeData"
                  valueText={themeData}
                  errors={errors}
                  setFieldValue={setFieldValue}
                  rows={10}
                />
              </VStack>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    );
  }, [themeData, errors]);

  return <>{view}</>;
};

interface AdvancedConfigurationSectionProps {
  noMembershipManagementService?: boolean;
  promotionId?: string | null;
  promotionConfigurationId?: string | null;
  existingMemberCheckFilterQueryString?: string | null;
  checkExistingMember?: boolean;
  businessId: string;
  countryOptions?: CherryHubAdminApi.EnumConfigurationItem[] | null;
  countryOptionsLoading: boolean;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

const AdvancedConfigurationSection = ({
  noMembershipManagementService,
  promotionConfigurationId,
  promotionId,
  existingMemberCheckFilterQueryString,
  checkExistingMember,
  businessId,
  countryOptions,
  countryOptionsLoading,
  setFieldValue,
}: AdvancedConfigurationSectionProps) => {
  const memberFilterQueryModal = useDisclosure();
  const onSuccessMemberFilterQuery = (query: string | undefined) => {
    setFieldValue("existingMemberCheckFilterQueryString", query);
    memberFilterQueryModal.onClose();
  };

  const view = useMemo(() => {
    return (
      <>
        <SwitchField
          horizontal
          label="No Membership Management Service"
          name="noMembershipManagementService"
        />

        <TextField horizontal label="PromotionId" name="promotionId" />

        <TextField
          horizontal
          label="Promotion Configuration Id"
          name="promotionConfigurationId"
        />

        <SwitchField
          horizontal
          label="Check Existing Member"
          name="checkExistingMember"
        />

        {checkExistingMember && (
          <>
            <TextArea
              horizontal
              label="Existing Member Check Filter Query String"
              name="existingMemberCheckFilterQueryString"
              isReadOnly
              rightElement={
                <Button
                  onClick={memberFilterQueryModal.onOpen}
                  alignSelf="flex-end"
                  colorScheme="cherryButton"
                >
                  Filters
                </Button>
              }
            />
            <SwitchField
              horizontal
              label="Allow Existing Member Check Override"
              name="allowExistingMemberCheckOverride"
              helperText="Not applicable for two step registrations."
            />
            <SwitchField
              horizontal
              label="Use Fuzzy Member Matching"
              name="useFuzzyMemberMatching"
            />
          </>
        )}

        <ChakraReactSelectField
          horizontal
          label="Geosuggest Country Filter"
          name="geoSuggestCountryFilter"
          isLoading={countryOptionsLoading}
          options={
            countryOptions?.map((provider) => {
              return {
                label: provider.Value!,
                value: provider.Key!,
              };
            }) || []
          }
          selectedOptionColor="blue"
          isMulti
        />
      </>
    );
  }, [
    noMembershipManagementService,
    promotionConfigurationId,
    promotionId,
    checkExistingMember,
    countryOptions,
    countryOptionsLoading,
  ]);

  return (
    <>
      {view}
      <MemberFilterQueryModal
        size="4xl"
        {...memberFilterQueryModal}
        onSuccess={onSuccessMemberFilterQuery}
        businessId={businessId}
        memberQueryFilter={existingMemberCheckFilterQueryString ?? ""}
      />
    </>
  );
};
